import Rails from '@rails/ujs';
import { Controller } from 'stimulus';
import Uppy from '@uppy/core';
import DragDrop from '@uppy/drag-drop';
import FileInput from '@uppy/file-input';
import ProgressBar from '@uppy/progress-bar';
import AwsS3Multipart from '@uppy/aws-s3-multipart';

import { getFileMetadata } from '../lib/getFileMetadata';
import convertToMp4Job from '../lib/convertToMp4Job';
import waitJobComplete from '../lib/waitJobComplete';
import buildFormData from '../lib/buildFormData';

export default class extends Controller {
  static get values() {
    return {
      dragDrop: Boolean,
      paramName: String,
      successUrl: String,
      acceptValues: String,
      allowBigFiles: Boolean,
      acceptValuesLabel: String,
    };
  }

  static get targets() {
    return ['input', 'spinner'];
  }

  inputName = `${this.paramNameValue}[file]`;

  fileInputConfig = {
    target: this.inputTarget,
    inputName: this.inputName,
    multiple: true,
    locale: {
      strings: {
        chooseFiles: 'Choose files (max 5)',
      },
    },
  };

  dragDropConfig = {
    target: this.inputTarget,
    inputName: this.inputName,
    multiple: true,
    locale: {
      strings: {
        dropHereOr: '%{browse} or drag and drop',
        browse: 'Upload files (max 5)',
      },
    },
    note: `${this.acceptValuesLabelValue || ''}`,
  };

  connect() {
    // Check if Uppy is already initialized in THIS specific element, not globally
    if (this.element.getElementsByClassName('uppy-Root').length) return;

    const restrictions = {
      minNumberOfFiles: 1,
      maxNumberOfFiles: 5,
    };

    if (this.acceptValuesValue) restrictions.allowedFileTypes = this.acceptValuesValue.split(',');

    this.uppy = new Uppy({
      debug: true,
      restrictions,
      autoProceed: this.dragDropValue,
    })
      .use(
        this.dragDropValue ? DragDrop : FileInput,
        this.dragDropValue ? this.dragDropConfig : this.fileInputConfig
      )
      .use(ProgressBar, {
        target: this.inputTarget,
      })
      .use(AwsS3Multipart, {
        companionUrl: '/',
      });
    // this.observeUppyLoad();

    if (this.allowBigFilesValue) {
      this.addPreviewAlert();
    }

    this.uppy.on('file-added', (file) => {
      if (this.dragDropValue) {
        this.addDragDropSpinner();
      } else {
        this.addFileInputName(file.data.name);
      }
    });

    this.uppy.on('upload', () => {
      this.showLoading();
    });

    this.uppy.on('upload-success', async (file) => {
      const metadata = await getFileMetadata(file);
      const data = buildFormData(metadata);

      Rails.ajax({
        url: '/uppy/create_blob',
        type: 'POST',
        data,
        success: async (signedId) => {
          try {
            this.showProcessing();
            await this.handleFileConvert(file);
            this.handleSubmit(file, signedId);
          } catch (err) {
            console.error(err);
            Turbo.visit(window.location.href);
          } finally {
            this.hideLoading();
          }
        },
        error: () => Turbo.visit(window.location.href),
      });
    });
  }
  observeUppyLoad() {
    const observer = new MutationObserver((mutations, obs) => {
      // Check if Uppy's root class is now in the DOM
      const uppyUI = this.inputTarget.querySelector('.uppy-DragDrop-inner');

      if (uppyUI) {
        console.log("Uppy UI is officially loaded!");
        this.replaceDefaultIcon();

        // Stop observing once we've found it and made our changes
        obs.disconnect();
      }
    });

    // Observe child additions in your input target
    observer.observe(this.inputTarget, {
      childList: true,
      subtree: true
    });
  }
  replaceDefaultIcon() {
    const iconContainer = this.element.querySelector('.uppy-DragDrop-inner');
    if (iconContainer) {
      iconContainer.innerHTML = `
       <svg width="39" height="49" viewBox="0 0 39 49" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M34.5 17L19.5 2M19.5 2L4.5 17M19.5 2V37M2 47H37" stroke="#576C89" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div class="uppy-DragDrop-label"><span class="uppy-DragDrop-browse">Upload a file</span> or drag and drop</div>
        <span class="uppy-DragDrop-note"></span>
      `;
    }
  }

  upload(e) {
    e.preventDefault();
    this.uppy.upload();
  }

  handleSubmit(file, signedId) {
    const data = buildFormData({ [this.inputName]: signedId, name: file.data.name });
    Rails.ajax({
      url: this.element.action,
      type: 'POST',
      data,
      success: () => Turbo.visit(this.successUrlValue || window.location.href),
      error: () => Turbo.visit(window.location.href),
    });
  }

  async handleFileConvert(file) {
    if (!file.type.includes('video')) return;

    if (file.type.includes('mp4')) {
      const { Job } = await convertToMp4Job(
        file.s3Multipart.key,
        file.s3Multipart.key.replace('.mp4', '-converted')
      );
      // We don't await here to avoid blocking the UI.
      waitJobComplete(Job.id).then(() => {
        console.log('Video conversion completed for:', file.s3Multipart.key);
      }).catch((err) => {
        console.error('Video conversion failed for:', file.s3Multipart.key, err);
      });
    } else {
      const { Job } = await convertToMp4Job(file.s3Multipart.key);
      // We don't await here to avoid blocking the UI.
      waitJobComplete(Job.id).then(() => {
        console.log('Video conversion completed for:', file.s3Multipart.key);
      }).catch((err) => {
        console.error('Video conversion failed for:', file.s3Multipart.key, err);
      });
    }
  }

  showLoading() {
    for (const child of this.element.elements) {
      child.disabled = true;
    }

    if (!this.hasSpinnerTarget) return;

    this.spinnerTarget.classList.remove('hidden');
    this.element.classList.add('cursor-wait');
  }

  showProcessing() {
    const span = this.element.querySelector('button[data-action="click->uppy#upload"] span');
    if (span) {
      span.textContent = 'Processing...';
    }
  }

  hideLoading() {
    for (const child of this.element.elements) {
      child.disabled = false;
    }

    if (!this.hasSpinnerTarget) return;

    this.spinnerTarget.classList.add('hidden');
    this.element.classList.remove('cursor-wait');
  }

  addDragDropSpinner() {
    const [browserElem] = document.getElementsByClassName('uppy-DragDrop-browse') || [];
    if (!browserElem) return;

    browserElem.innerHTML = `
      <svg data-uppy-target="spinner" class="hidden animate-spin mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>`;
  }

  addFileInputName(name) {
    const [fileInputBtn] = document.getElementsByClassName('uppy-FileInput-btn') || [];
    if (!fileInputBtn) return;

    fileInputBtn.insertAdjacentHTML(
      'afterend',
      `
      <span class="w-full text-gray-900 text-sm font-medium overflow-hidden no-wrap">
        ${name}
      </span>`
    );
  }

  addPreviewAlert() {
    const [noteElem] = document.getElementsByClassName('uppy-DragDrop-note') || [];
    const [dragDropInnerElem] = document.getElementsByClassName('uppy-DragDrop-inner') || [];
    if (!noteElem || !dragDropInnerElem) return;
    const alertElem = noteElem.cloneNode(true);
    alertElem.innerHTML =
      'Note: When uploading a large file, it may take up to a few minutes for the thumbnail to be generated';
    dragDropInnerElem.appendChild(alertElem);
  }
}
